import BomArrow from "@assets/images/bom-arrow.svg";
import duplicateIcon from "@assets/images/duplicate.svg";
import pencilIcon from "@assets/images/pencil.svg";
import SearchIconNew from "@assets/images/Search-new.svg";
import TrashCan from "@assets/images/trash.svg";
import undoCancel from "@assets/images/undo-icon.svg";
import XCircleOutLine from "@assets/images/x-circle-outline.svg";
import { Button, Grid, IconButton, Typography } from "@material-ui/core";
import DatePickerApplyAll from "@saleor/components/DatePickerApplyAll";
import DecimalFormat from "@saleor/components/DecimalFormat";
import { FIELD_TYPE } from "@saleor/components/FormField/constants";
import FormField from "@saleor/components/FormField/FormField";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat";
import SingleSelectField from "@saleor/components/SingleSelectField";
import SingleTextField from "@saleor/components/SingleTextField";
import SingleTextFieldV2 from "@saleor/components/SingleTextFieldV2/SingleTextFieldV2";
import TextAreaField from "@saleor/components/TextAreaField";
import { REGEX_ONLY_NUMBER } from "@saleor/config";
import PlantPopOver from "@saleor/createOrChangeNotRefContract/components/ApplyAllPopOver/PlantPopOver";
import { getSalesPartnerMapping } from "@saleor/createOrChangeNotRefContract/components/HeaderTab/Tab/NotRefSalesPartner/utils";
import ModalSearchForPlants from "@saleor/createOrChangeNotRefContract/components/ModalSearchForPlants";
import PoItemPopover from "@saleor/createOrChangeNotRefContract/components/PoItemPopover";
import { ITEM_STATUS } from "@saleor/createOrChangeNotRefContract/constants";
import {
  applyAllCheck,
  deriveShipToBillTo,
  getShipToData
} from "@saleor/createOrChangeNotRefContract/helpers";
import {
  BATCH_FLAG_CHOICES,
  BATCH_NO
} from "@saleor/createOrChangeNotRefContract/utils";
import useDebounce from "@saleor/hooks/useDebounce";
import classNames from "classnames";
import { format, isValid, parse } from "date-fns";
import moment from "moment-timezone";
import React, { useState } from "react";
import InlineSVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import useStyles from "./styles";

const nonEmptyFields = new Set(["materialCode", "itemNo"]);
const emptyMaterialFeilds = new Set([
  "deliveryTolerance",
  "poItemNumber",
  "poDetails",
  "requestDate",
  "plant",
  "pricePerUnit",
  "priceCurrency",
  "netValue",
  "paymentTermItem",
  "shipTo",
  "shippingPoint"
]);
const notRefSalesUnit = new Set(["TON", "KG"]);

const emptyMiddleware = (
  formatter,
  row: NotRefOrderLine,
  col,
  index,
  mode?: Imode
) => {
  const isPriceStatusDescField =
    row?.priceStatus === "E" && row?.priceStatusDesc;
  let filteredEmptyMaterialFeilds = emptyMaterialFeilds;
  if (isPriceStatusDescField) {
    const filteredArray = Array.from(emptyMaterialFeilds).filter(
      field => !["pricePerUnit", "priceCurrency", "netValue"].includes(field)
    );
    filteredEmptyMaterialFeilds = new Set(filteredArray);
  }

  const isBomMatEmpty = (bom, cell) =>
    bom && filteredEmptyMaterialFeilds.has(cell);

  if (
    (!row?.materialCode && !nonEmptyFields.has(col.id)) ||
    (row?.materialCode && isBomMatEmpty(row?.parentItemNo, col?.id))
  ) {
    if (col.child) {
      return null;
    }
    return (
      <MyTypography variant="subtitle3" align={col?.align || "center"}>
        -
      </MyTypography>
    );
  } else {
    return formatter?.(row, col, index, mode);
  }
};

const rendrViewContent = value =>
  value || <MyTypography variant="subtitle3">-</MyTypography>;
const rendrViewDate = value => {
  if (value) {
    const parseDate = parse(value, "yyyy-MM-dd", new Date());
    if (isValid(parseDate)) {
      return format(parseDate, "dd/MM/yyyy");
    } else {
      return value;
    }
  } else {
    return <MyTypography variant="subtitle3">-</MyTypography>;
  }
};

const CommonDecimalFormatter = (value, countDecimal = 3) => (
  <DecimalFormat
    value={value}
    isGrayDecimal={false}
    countDecimal={countDecimal}
  />
);

const CommonSelectField = (
  name,
  value,
  options,
  handleChange,
  classes,
  width = 88,
  id = "",
  disabled = false
) => (
  <SingleSelectField
    selectProps={{ style: { height: 32, width } }}
    InputProps={{ classes: { input: classes.singleselect } }}
    choices={options}
    onChange={handleChange}
    placeholder={"Select"}
    value={value}
    name={name}
    id={id}
    disabled={disabled}
  />
);

const CommonTextField = ({
  name,
  value,
  handleChange,
  classes,
  className = "",
  disabled = false,
  placeholder = "",
  maxLengthProps = 0,
  error,
  id = ""
}) => (
  <div style={{ height: 32 }}>
    <SingleTextField
      id={id}
      name={name}
      value={value}
      disabled={disabled}
      onChange={handleChange}
      className={classNames(classes.commonText, className)}
      {...(maxLengthProps ? { maxLengthProps } : {})}
      placeholder={placeholder}
      InputProps={{
        inputProps: { "data-testid": id }
      }}
      error={!!error}
    />
  </div>
);

const CommonNumberField = ({
  name,
  value,
  handleChange,
  classes,
  allowLeadingZeros = true,
  fixedDecimalScale = true,
  placeholder = "0",
  decimalScale = 0,
  disabled = false,
  onComplete,
  isAllowed = null,
  error = "",
  id = ""
}) => {
  const onChange = e => {
    handleChange(e);
    onComplete();
  };
  return (
    <div className={classes.boxQuantity}>
      <NumberFormat
        customClass={classes.numberInput}
        id={id}
        name={name}
        onChange={onChange}
        value={value}
        rightAlignPlaceholder={true}
        allowNegative={false}
        allowLeadingZeros={allowLeadingZeros}
        fixedDecimalScale={fixedDecimalScale}
        placeholder={placeholder}
        decimalScale={decimalScale}
        error={!!error}
        isAllowed={isAllowed}
        disabled={disabled}
        inputDisabled={disabled}
      />
      {!!error?.trim() && <div className={classes.errorText}>{error}</div>}
    </div>
  );
};

const ItemNoFormatter = (row: NotRefOrderLine, cell) => (
  <MyTypography
    variant={row?.parentItemNo ? "body3" : "subtitle3"}
    style={
      row?.materialType !== "85" && row?.priceStatus === "E"
        ? { color: "red" }
        : {}
    }
  >
    {row?.isSplit ? "-" : row[cell.id]}
  </MyTypography>
);

function useFormatters({
  handleOpenSearchModal,
  handleOpenShipToModal,
  formik,
  formikHeader,
  handleSplit,
  deleteChangeOrderLines,
  isChangeFlow,
  undoCancelOrderLine,
  orderTypeS,
  disabledActionForCashSale,
  handleOpenSaleTextModal
}) {
  const classes = useStyles();
  const intl = useIntl();
  const handleChange = useDebounce(formik.handleChange, 100);

  const customOnChange = row => e => {
    handleChange(e);
    formik.handleChange({
      target: {
        name: "currentIndex",
        value: row?.indexNumber
      }
    });
  };

  const handleApplyAllRequestDate = (
    value,
    orderTypeCashSale,
    currentAction
  ) => {
    formik.setValues(prev => ({
      ...prev,
      lines: prev.lines.map(v => ({
        ...v,
        requestDate: applyAllCheck(
          value,
          "requestDate",
          orderTypeCashSale,
          currentAction,
          v,
          isChangeFlow
        )
      }))
    }));
  };

  const handleApplyAllPriceDate = (value, orderTypeCashSale, currentAction) => {
    formik.setValues(prev => ({
      ...prev,
      lines: prev.lines.map(v => ({
        ...v,
        priceDate: applyAllCheck(
          value,
          "priceDate",
          orderTypeCashSale,
          currentAction,
          v,
          isChangeFlow
        )
      }))
    }));
  };

  const CommonDateField = ({
    name,
    value,
    handleChange,
    onComplete,
    applyValidation = false,
    minDate = undefined,
    disableFutureDate = false,
    disable = null,
    error,
    id = ""
  }) => {
    const onChange = e => {
      handleChange(e);
      onComplete(e.target.value);
    };

    return (
      <>
        <Grid container style={{ height: 32 }}>
          <FormField
            id={id}
            name={name}
            type={FIELD_TYPE.DATEPICKER}
            value={value}
            isUpdateDate={true}
            handleChange={onChange}
            md={12}
            sm={12}
            disableManualDateInput={true}
            error={!!error && applyValidation}
            minDate={minDate}
            disableFutureDate={disableFutureDate}
            isDisabled={disable}
          />
        </Grid>
        {!!error && applyValidation && (
          <div className={classes.errorText}>{error}</div>
        )}
      </>
    );
  };

  const CommonSingleTextFieldUnblock = (
    name,
    value,
    handleChange,
    classes,
    placeHolder,
    disabled,
    onKeyPress,
    id = ""
  ) => (
    <SingleTextFieldV2
      id={id}
      name={name}
      value={value}
      onChange={handleChange}
      className={classes.inputUnlimit}
      placeholder={placeHolder}
      numberLimit={100}
      regex={REGEX_ONLY_NUMBER}
      disabled={disabled}
      onKeyPress={onKeyPress}
    />
  );

  const materialCodeFormatter = (
    row: NotRefOrderLine,
    col: TableColumn,
    _,
    mode: Imode,
    isChangeFlow
  ) => (
    <div>
      <div className={classes.rowFlex}>
        {row.parentItemNo ? (
          <div style={{ marginLeft: !isChangeFlow ? 8 : "-10px" }}>
            <InlineSVG
              src={BomArrow}
              width={24}
              height={24}
              className={mode?.sortDirection === "asc" ? "" : classes?.flip}
            />
          </div>
        ) : (
          !isChangeFlow && (
            <Button
              id={`searchMaterialCodeModal-${row?.indexNumber}`}
              data-testid="searchMaterialCodeButton"
              variant={"contained"}
              color={"secondary"}
              className={classes.btnExternal}
              onClick={() => handleOpenSearchModal(row?.itemNo, row?.id)}
            >
              <InlineSVG src={SearchIconNew} width={24} height={24} />
            </Button>
          )
        )}
        <MyTypography variant={row.parentItemNo ? "body3" : "subtitle3"}>
          {row[col.id] || <span style={{ color: "#999" }}>ค้นหาสินค้า</span>}
        </MyTypography>
      </div>
    </div>
  );

  const materialDescriptionFormatter = (row, cell, _, mode?: Imode) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}
    >
      <div>
        <MyTypography variant={row?.parentItemNo ? "body3" : "subtitle3"}>
          {row[cell.id]}
        </MyTypography>
        {mode?.displayView && (
          <MyTypography
            variant={"caption"}
            className={classNames(
              {
                [classes.boxCancel]: row?.itemStatusEn === ITEM_STATUS.CANCEL
                // [classes.boxDelete]: item.statusCancel === "Delete"
              },
              "canceled"
            )}
            style={{ width: "auto", display: "block" }}
          >
            {row?.itemStatusEn === ITEM_STATUS.CANCEL && (
              <Typography variant="caption">
                <div>Cancelled (93)</div>
              </Typography>
            )}
          </MyTypography>
        )}
      </div>

      <div
        style={{
          margin: "5px 5px 0 10px",
          cursor: "pointer",
          minWidth: mode?.displayView && !mode?.isDisabled ? 0 : 34
        }}
      >
        {isChangeFlow &&
          !mode?.isDisabled &&
          !row?.parentItemNo &&
          !row?.isNewItem &&
          (!mode?.displayView || mode?.currentAction === "split") && (
            <Button
              id={
                row?.isSplit
                  ? `cancelItem-${row?.indexNumber}`
                  : `duplicateItem-${row?.indexNumber}`
              }
              data-testid={
                row?.isSplit
                  ? `cancelItem-${row?.indexNumber}`
                  : `duplicateItem-${row?.indexNumber}`
              }
              className={classes.iconBtn}
              disabled={
                (row?.splitOriginal && row?.quantity <= 0) ||
                !row?.isSplitEnabled
              }
              onClick={() => handleSplit(row?.indexNumber, row?.isSplit)}
            >
              <InlineSVG src={row?.isSplit ? XCircleOutLine : duplicateIcon} />
            </Button>
          )}
      </div>
      {row?.isNewItem && !row?.parentItemNo && (
        <div style={{ margin: "5px 5px 0 10px", cursor: "pointer" }}>
          <InlineSVG
            id={`materialDeleteButton-${row?.indexNumber}`}
            onClick={() => deleteChangeOrderLines(row?.itemNo)}
            src={TrashCan}
            height={18}
            width={18}
          />
        </div>
      )}

      {ITEM_STATUS.CANCEL === row?.itemStatusEn &&
        !mode.isViewMode &&
        !disabledActionForCashSale && (
          <div
            style={{
              margin: "5px 5px 0 10px",
              cursor:
                mode?.currentAction === "none" || mode?.currentAction === "edit"
                  ? "pointer"
                  : "default",
              pointerEvents:
                mode?.currentAction === "none" || mode?.currentAction === "edit"
                  ? "auto"
                  : "none",
              opacity:
                mode?.currentAction === "none" || mode?.currentAction === "edit"
                  ? 1
                  : 0.5
            }}
          >
            <InlineSVG
              id={`materialUndoButton-${row?.indexNumber}`}
              data-testid="materialUndoButton"
              onClick={() => undoCancelOrderLine(row?.itemNo, row?.so)}
              src={undoCancel}
              height={18}
              className={classes.undCancelBtn}
              width={18}
            />
          </div>
        )}
    </div>
  );

  function quantityLocalValidator(quantity) {
    if (+quantity >= 100000) {
      return "กรุณาตรวจสอบจำนวนอีกครั้ง";
    } else {
      return null;
    }
  }

  const quantityFormatter = (row: NotRefOrderLine, col, _, mode?: Imode) => {
    const isReadOnly =
      row?.parentItemNo ||
      (mode?.displayView && !row?.isSplit) ||
      (!mode?.displayView &&
        isChangeFlow &&
        mode?.currentAction !== "add" &&
        col?.allowEdit?.has(row?.itemStatusEn));

    const isDisabled =
      isReadOnly || (!row?.isSplit && disabledActionForCashSale);

    return isReadOnly ? (
      CommonDecimalFormatter(
        +row?.[col?.id],
        notRefSalesUnit.has(row?.salesUnit) ? 3 : 0
      )
    ) : (
      <CommonNumberField
        id={`lines[${row?.indexNumber}].${col.id}Field`}
        name={`lines[${row?.indexNumber}].${col.id}`}
        value={row[col.id]}
        handleChange={handleChange}
        classes={classes}
        allowLeadingZeros={notRefSalesUnit.has(row?.salesUnit)}
        fixedDecimalScale={notRefSalesUnit.has(row?.salesUnit)}
        placeholder="ระบุจำนวน"
        decimalScale={notRefSalesUnit.has(row?.salesUnit) ? 3 : 0}
        disabled={isDisabled}
        onComplete={() => {
          formik.setFieldValue("currentIndex", row?.indexNumber);
        }}
        error={mode?.error || quantityLocalValidator(row[col.id])}
      />
    );
  };

  const BatchFormatterComp = ({ row, col, error }) => {
    const [batchChoice, setBatchChoice] = useState(
      row?.batchChoiceFlag || BATCH_FLAG_CHOICES.TEXT
    );

    if (
      row[col.id]?.toUpperCase().trim() === BATCH_NO.NO_PRINT &&
      row?.batchChoiceFlag === BATCH_FLAG_CHOICES.BLANK
    ) {
      row.batchNo = "";
      row.batchChoiceFlag = BATCH_FLAG_CHOICES.BLANK;
    }

    const handleBatchChange = e => {
      const newValue = e?.target?.value;
      formik.setValues(prev => {
        const updatedLines = prev.lines.map(line => {
          if (line?.id !== row?.id) {
            return line;
          }
          return {
            ...line,
            batchNo:
              newValue === BATCH_FLAG_CHOICES.BLANK ? BATCH_NO.NO_PRINT : "",
            batchChoiceFlag: newValue
          };
        });
        return { ...prev, lines: updatedLines };
      });
      setBatchChoice(newValue);
    };

    const options = [
      {
        value: BATCH_FLAG_CHOICES.TEXT,
        label: CommonTextField({
          id: `lines[${row?.indexNumber}].${col.id}Field`,
          name: `lines[${row?.indexNumber}].${col.id}`,
          value: row[col.id],
          handleChange,
          classes,
          className: classes.batchText,
          disabled: batchChoice === BATCH_FLAG_CHOICES.BLANK,
          error: error && batchChoice !== BATCH_FLAG_CHOICES.BLANK,
          placeholder: "ระบุ Batch",
          maxLengthProps: 10
        })
      },
      {
        value: BATCH_FLAG_CHOICES.BLANK,
        label: BATCH_NO.NO_PRINT
      }
    ];
    if (row.batchFlag && !(row?.bomFlag && !row?.parentItemNo)) {
      return (
        <Grid container style={{ height: 30 }}>
          <FormField
            id={`lines[${row?.indexNumber}].${col.id}batchChoiceFlag`}
            className={classes.batchRadio}
            type={FIELD_TYPE.RADIO_GROUP}
            name={`lines[${row?.indexNumber}].${col.id}batchChoiceFlag`}
            value={batchChoice}
            placeholder="ระบุ Batch"
            options={options}
            md={12}
            handleChange={handleBatchChange}
          />
        </Grid>
      );
    }

    return (
      <MyTypography align="center" variant="subtitle3">
        -
      </MyTypography>
    );
  };

  const rendrBatchViewContent = (row, col) => {
    let batchContent = row?.[col?.id];
    if (
      row?.batchFlag &&
      row?.batchChoiceFlag === BATCH_FLAG_CHOICES.BLANK &&
      !batchContent
    ) {
      batchContent = BATCH_NO.NO_PRINT;
    }
    return batchContent || <MyTypography variant="subtitle3">-</MyTypography>;
  };

  const batchNumberFormatter = (row: NotRefOrderLine, col, _, mode?: Imode) =>
    mode?.displayView ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn)) ? (
      rendrBatchViewContent(row, col)
    ) : (
      <BatchFormatterComp
        row={row}
        col={col}
        error={mode?.error}
        key={row?.batchChoiceFlag}
      />
    );

  const quantityUnitFormatter = (
    row: NotRefOrderLine,
    col,
    _,
    mode?: Imode
  ) => {
    if (
      mode?.displayView ||
      (!mode?.displayView &&
        isChangeFlow &&
        mode?.currentAction !== "add" &&
        col?.allowEdit?.has(row?.itemStatusEn))
    ) {
      return rendrViewContent(row?.[col?.id]);
    }
    const options =
      (Array.isArray(row?.salesUnitList) &&
        row?.salesUnitList?.map(unit => ({
          label: unit.toUnit,
          value: unit.toUnit
        }))) ||
      [];
    const onChange = e => {
      handleChange(e);
      formik.handleChange({
        target: {
          name: "currentIndex",
          value: row?.indexNumber
        }
      });
    };
    return (
      <>
        {CommonSelectField(
          `lines[${row?.indexNumber}].${col.id}`,
          row[col.id],
          options,
          onChange,
          classes,
          71,
          `lines[${row?.indexNumber}].${col.id}Field`,
          disabledActionForCashSale
        )}
      </>
    );
  };

  const usePlantSearchFormatter = (row, col, _, mode?: Imode) =>
    mode?.displayView ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      ((row?.bomFlag && !row?.parentItemNo) ||
        col?.allowEdit?.has(row?.itemStatusEn))) ? (
      rendrViewContent(row?.[col?.id])
    ) : (
      <ModalSearchForPlants
        row={row}
        col={col}
        classes={classes}
        handleChange={customOnChange(row)}
      />
    );

  const printPrice = price => {
    if (price) {
      return (
        <MyTypography variant="body3" align="right">
          {CommonDecimalFormatter(+price, 2)}
        </MyTypography>
      );
    } else {
      return (
        <MyTypography variant="subtitle3" align="center">
          -
        </MyTypography>
      );
    }
  };
  const pricePerUnitFormatter = (
    row: NotRefOrderLine,
    col,
    _,
    mode?: Imode
  ) => {
    if (
      mode?.displayView ||
      (!mode?.displayView &&
        isChangeFlow &&
        mode?.currentAction !== "add" &&
        col?.allowEdit?.has(row?.itemStatusEn))
    ) {
      return printPrice(row?.[col?.id]);
    }
    if (row?.materialType === "85" && row?.manualPriceFlag) {
      return (
        <CommonNumberField
          id={`lines[${row?.indexNumber}].${col.id}Field`}
          name={`lines[${row?.indexNumber}].${col.id}`}
          value={row[col.id]}
          handleChange={handleChange}
          classes={classes}
          allowLeadingZeros={false}
          fixedDecimalScale={true}
          placeholder=""
          decimalScale={2}
          onComplete={() => {
            formik.handleChange({
              target: {
                name: "currentIndex",
                value: row?.indexNumber
              }
            });
          }}
          error={mode?.error}
        />
      );
    } else {
      if (row?.pricePerUnit) {
        return printPrice(row?.[col?.id]);
      } else if (row?.priceStatus === "E" && row?.priceStatusDesc) {
        return (
          <MyTypography variant="body3" color="error">
            {row?.priceStatusDesc}
          </MyTypography>
        );
      } else {
        return <span>-</span>;
      }
    }
  };
  const netPriceFormatter = (row, col) => {
    if (row[col.id] || row?.isSplit || row?.splitOriginal) {
      return CommonDecimalFormatter(+row[col.id], 2);
    } else {
      return row?.priceStatus === "E" && row?.priceStatusDesc ? (
        <MyTypography variant="body3" color="error">
          {row?.priceStatusDesc}
        </MyTypography>
      ) : (
        <span>-</span>
      );
    }
  };

  const requestDateFormatter = (row, col, _, mode?: Imode) =>
    (mode?.displayView && !row?.isSplit) ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn)) ? (
      rendrViewDate(row?.[col?.id])
    ) : (
      <CommonDateField
        name={`lines[${row?.indexNumber}].${col.id}`}
        value={row[col.id]}
        handleChange={handleChange}
        onComplete={() =>
          formik.handleChange({
            target: {
              name: "currentIndex",
              value: row?.indexNumber
            }
          })
        }
        error={mode?.error}
        applyValidation={true}
        minDate={moment()}
        disable={
          !orderTypeS &&
          isChangeFlow &&
          mode?.currentAction !== "add" &&
          row?.itemStatusEn === ITEM_STATUS.COMPLETE_DELIVERY
        }
        id={`lines[${row?.indexNumber}].${col?.id}Field`}
      />
    );

  const originalDateFormatter = (row, col) => (
    <MyTypography variant="body3">
      {row?.[col.id] ? (
        rendrViewDate(row?.[col?.id])
      ) : (
        <span style={{ fontWeight: "bold" }}>-</span>
      )}
    </MyTypography>
  );
  const priceDateCheck = (row, col, mode) =>
    (!(isChangeFlow && mode?.currentAction !== "add") && row?.parentItemNo) ||
    (isChangeFlow &&
      mode?.currentAction !== "add" &&
      row?.bomFlag &&
      !row?.parentItemNo) ||
    mode?.displayView ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn));

  const priceDateFormatter = (row, col, _, mode?: Imode) =>
    priceDateCheck(row, col, mode) ? (
      !(isChangeFlow && mode?.currentAction !== "add") && row?.parentItemNo ? (
        rendrViewContent("")
      ) : (
        rendrViewDate(row?.[col?.id])
      )
    ) : (
      <CommonDateField
        name={`lines[${row?.indexNumber}].${col.id}`}
        value={row[col.id]}
        handleChange={handleChange}
        onComplete={() =>
          formik.handleChange({
            target: {
              name: "currentIndex",
              value: row?.indexNumber
            }
          })
        }
        error={mode?.error}
        id={`lines[${row?.indexNumber}].${col?.id}Field`}
      />
    );

  const itemCatFormater = (row, col) =>
    row?.bomFlag && !row?.parentItemNo
      ? rendrViewContent(false)
      : rendrViewContent(row?.[col?.id]);

  const paymentTermFormatter = (
    row,
    col,
    _,
    mode?: Imode,
    paymentTermList = []
  ) =>
    mode?.displayView ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      row?.bomFlag &&
      !row?.parentItemNo) ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn))
      ? rendrViewContent(row?.paymentTermItem?.split("-")?.[0]?.trim())
      : CommonSelectField(
          `lines[${row?.indexNumber}].${col.id}`,
          row?.paymentTermItem?.split("-")?.[0]?.trim(),
          getSalesPartnerMapping(paymentTermList),
          handleChange,
          classes,
          104,
          `lines[${row?.indexNumber}].${col.id}Field`
        );

  const poDetailsFormatter = (row, col, _, mode?: Imode) =>
    mode?.displayView ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn)) ? (
      rendrViewContent(row[col.id] ?? formikHeader.values.poNumber)
    ) : (
      <PoItemPopover
        classes={classes}
        row={row}
        col={col}
        handleChange={handleChange}
        formikHeader={formikHeader}
      />
    );

  const poItemNumberFormatter = (row, col, _, mode?: Imode) =>
    mode?.displayView ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn))
      ? rendrViewContent(row?.[col?.id])
      : CommonTextField({
          name: `lines[${row?.indexNumber}].${col.id}`,
          value: row[col.id],
          handleChange,
          classes,
          className: "",
          disabled: false,
          placeholder: "",
          error: null,
          maxLengthProps: 6,
          id: `lines[${row?.indexNumber}].${col.id}Field`
        });

  const changeOrderOsFormatter = (row, col) =>
    (row?.bomFlag && !row?.parentItemNo) ||
    (row?.bomFlag && row?.parentItemNo && row?.materialType !== "82")
      ? rendrViewContent(false)
      : rendrViewContent(row?.[col?.id]);

  const changeOrderOsHeaderFormatter = title => (
    <>
      <MyTypography variant={"subtitle3"} style={{ position: "relative" }}>
        {title}
      </MyTypography>
      <span style={{ fontWeight: "normal" }}>(OS)</span>
    </>
  );

  const changeOrderItemOsHeaderFormatter = title => (
    <>
      <span style={{ fontWeight: "bold", position: "relative" }}>{title}</span>
      <span style={{ fontWeight: "normal" }}>(OS)</span>
    </>
  );

  const productionFlagFormatter = (row, col, _, mode?: Imode) => {
    if (
      (mode?.displayView && (!row?.isSplit || row?.parentItemNo)) ||
      (!mode?.displayView &&
        mode?.currentAction !== "add" &&
        isChangeFlow &&
        col?.allowEdit?.has(row?.itemStatusEn))
    ) {
      return rendrViewContent(row?.[col?.id]);
    }
    const options = [
      {
        label: <MyTypography variant={"body3"}>ผลิต</MyTypography>,
        value: "ผลิต"
      },
      {
        label: <MyTypography variant={"body3"}>ไม่ผลิต</MyTypography>,
        value: "ไม่ผลิต"
      }
    ];
    return CommonSelectField(
      `lines[${row?.indexNumber}].${col.id}`,
      row[col.id],
      options,
      customOnChange(row),
      classes,
      95,
      `lines[${row?.indexNumber}].${col.id}Field`
    );
  };

  const unitTextFormatter = row => (
    <span>{` ${row?.weightUnit} / ${row?.saleQtyDivision} ${row?.salesUnit}`}</span>
  );

  const unitOfMeasureFormatter = (row, col, _, mode?: Imode) =>
    (row?.bomFlag && !row?.parentItemNo) ||
    mode?.displayView ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn)) ? (
      rendrViewContent(
        row?.[col?.id] && !(row?.bomFlag && !row?.parentItemNo) ? (
          <>
            <span>{row?.[col?.id]}</span>
            {row?.saleQtyDivision ? (
              unitTextFormatter(row)
            ) : (
              <span style={{ marginLeft: "45px" }}>-</span>
            )}
          </>
        ) : (
          ""
        )
      )
    ) : (
      <div style={{ display: "flex", flexWrap: "nowrap" }}>
        {isChangeFlow && mode?.currentAction !== "add" && (
          <MyTypography
            variant="body3"
            align="left"
            style={{ paddingRight: "6px" }}
          >
            {CommonTextField({
              name: `lines[${row?.indexNumber}].${col.id}`,
              value: row[col.id],
              disabled: false,
              handleChange,
              classes,
              className: classes.inputtextField,
              placeholder: "",
              error: mode?.error,
              id: `lines[${row?.indexNumber}].${col.id}Field`
            })}
          </MyTypography>
        )}
        <MyTypography variant="body3" align="left" style={{ marginTop: "5px" }}>
          {row?.saleQtyDivision ? (
            unitTextFormatter(row)
          ) : (
            <span style={{ marginLeft: "45px" }}>-</span>
          )}
        </MyTypography>
      </div>
    );

  const unitOfMeasureHeaderFormatter = (
    mode: Imode,
    isChangeFlow,
    currentAction
  ) => (
    <MyTypography variant={"subtitle3"} style={{ position: "relative" }}>
      {intl.formatMessage({ defaultMessage: "Unit of measure" })}
      {isChangeFlow && currentAction === "add" ? (
        <span></span>
      ) : (
        !mode?.isViewMode && <span className={classes.required}>*</span>
      )}
    </MyTypography>
  );

  const requiredHeaderFormatter = col => (
    <MyTypography variant={"subtitle3"} style={{ position: "relative" }}>
      {col?.label}
      <span className={classes.required}>*</span>
    </MyTypography>
  );

  const itemStatusFormatter = (row, col) =>
    !row?.isSplit ? (
      rendrViewContent(row?.[col?.id])
    ) : (
      <span style={{ fontWeight: "bold" }}>-</span>
    );
  const renderDeliveryBlock = value => (value ? "Blocked" : "Unblock");
  const deliveryBlockedFormatter = (row, col) => (
    <MyTypography variant="body3">
      {!row?.isSplit ? (
        renderDeliveryBlock(row?.[col.id])
      ) : (
        <span style={{ fontWeight: "bold" }}>-</span>
      )}
    </MyTypography>
  );

  const shippingPointFormatter = (row, col, _, mode?: Imode) =>
    mode?.displayView ||
    mode?.currentAction === "add" ||
    (!mode?.displayView &&
      isChangeFlow &&
      ((row?.bomFlag && !row?.parentItemNo) ||
        col?.allowEdit?.has(row?.itemStatusEn)))
      ? rendrViewContent(row?.[col?.id])
      : CommonTextField({
          name: `lines[${row?.indexNumber}].${col.id}`,
          value: row[col.id],
          handleChange,
          classes,
          className: "",
          disabled: false,
          placeholder: "",
          maxLengthProps: 4,
          error: null,
          id: `lines[${row?.indexNumber}].${col.id}Field`
        });

  const textBoxFormatter = (maxLength = 132) => (
    row: ChangeNotRefOrderLine,
    col,
    _,
    mode?: Imode
  ) =>
    (mode?.displayView && !row?.isSplit) ||
    (!mode?.displayView &&
      !row?.isSplit &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn)) ? (
      rendrViewContent(row?.[col?.id])
    ) : (
      <TextAreaField
        placeHolder={col.label}
        maxLength={maxLength}
        className={classes.additionaltxtarea}
        disabled={false}
        verticalResizingOnly
        name={`lines[${row?.indexNumber}].${col.id}`}
        onChange={handleChange}
        value={row[col.id]}
        id={`lines[${row?.indexNumber}].${col.id}Field`}
      />
    );

  const ShipToPartySelection = ({ row }) => (
    <div
      style={{
        display: "flex",
        flexWrap: "nowrap",
        justifyContent: "space-between"
      }}
    >
      <MyTypography
        variant="body3"
        align="left"
        data-testid={`shipToPartyText-${row?.indexNumber}`}
      >
        {deriveShipToBillTo(getShipToData(row, formikHeader))}
      </MyTypography>
      <IconButton
        id={`shipToPartySelection-${row?.indexNumber}`}
        data-testid={`shipToPartySelection-${row?.indexNumber}`}
        color="primary"
        className={classes.svgIconButton}
        disableRipple
        disableFocusRipple
        disableTouchRipple
        onClick={() => handleOpenShipToModal(row?.indexNumber, null)}
      >
        <InlineSVG src={pencilIcon} height={18} width={18} />
      </IconButton>
    </div>
  );

  const shipToPartyFormatter = (row, col, _, mode?: Imode) =>
    mode?.displayView ||
    (!mode?.displayView &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn)) ? (
      rendrViewContent(deriveShipToBillTo(getShipToData(row, formikHeader)))
    ) : (
      <ShipToPartySelection row={row} />
    );

  const lotNoPpFormatter = (row: NotRefOrderLine, cell, _, mode?: Imode) =>
    (mode?.displayView && !row?.isSplit) ||
    cell?.allowEdit?.has(row?.itemStatusEn)
      ? rendrViewContent(row?.[cell?.id])
      : CommonTextField({
          name: `lines[${row?.indexNumber}].${cell.id}`,
          value: row[cell.id],
          handleChange,
          classes,
          className: "",
          disabled: false,
          placeholder: "Lot No. (PP)",
          maxLengthProps: 132,
          error: null,
          id: `lines[${row?.indexNumber}].${cell.id}Field`
        });

  const CIPPPFormatter = (col, suff) => (
    <>
      {col.label}
      <span className={classes.cippphead}> ({suff})</span>
    </>
  );

  const deliveryToleranceHeaderFormatter = row => (
    <div>
      <div>
        <MyTypography variant={"subtitle3"}>{row.label}</MyTypography>
      </div>
      <div className={classes.toleranceStyle}>
        <MyTypography
          variant="body3"
          align="left"
          style={{ marginLeft: "-15px" }}
        >
          Under (%)
        </MyTypography>

        <MyTypography
          variant="body3"
          align="left"
          style={{ marginRight: "-8px" }}
        >
          Over (%)
        </MyTypography>
      </div>
    </div>
  );
  const plantApplyAllHeaderFormatter = (
    row,
    _,
    mode: Imode,
    orderTypeCashSale
  ) => (
    <PlantPopOver
      classes={classes}
      row={row}
      formik={formik}
      isViewMode={mode?.isViewMode}
      orderTypeCashSale={orderTypeCashSale}
      currentAction={mode.currentAction}
      isChangeFlow={isChangeFlow}
      isLinesEmpty={mode?.isLinesEmpty}
    />
  );
  const requestDateApplyAllHeaderFormatter = (
    _,
    __,
    mode: Imode,
    orderTypeCashSale
  ) => (
    <MyTypography variant={"subtitle3"} className={classes.boxPlant}>
      {intl.formatMessage({ defaultMessage: "Request Date" })}
      {!mode?.isViewMode && (
        <>
          <span className={classes.required}>*</span>
          <DatePickerApplyAll
            onApplyAll={value =>
              handleApplyAllRequestDate(
                value,
                orderTypeCashSale,
                mode?.currentAction
              )
            }
            disabled={mode?.isLinesEmpty || mode?.disableRequestDate}
          />
        </>
      )}
    </MyTypography>
  );

  const priceDateApplyAllHeaderFormatter = (
    _,
    __,
    mode: Imode,
    orderTypeCashSale
  ) => (
    <MyTypography variant={"subtitle3"} className={classes.boxPlant}>
      {intl.formatMessage({ defaultMessage: "Price Date" })}
      {!mode?.isViewMode && (
        <DatePickerApplyAll
          onApplyAll={value =>
            handleApplyAllPriceDate(
              value,
              orderTypeCashSale,
              mode?.currentAction
            )
          }
          disabled={mode?.isLinesEmpty}
        />
      )}
    </MyTypography>
  );

  const disableDeliveryTolerance = () =>
    formikHeader?.values?.orderType === "ZP20" ||
    formikHeader?.values?.orderType === "ZBV";

  const handleKeyDown = event => {
    if (
      !/^(100|[1-9]?\d)$/.test(event.key) &&
      event.key !== "Backspace" &&
      event.key !== "Delete" &&
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight"
    ) {
      event.preventDefault();
    }
  };

  const deliveryToleranceFormatter = (
    row: NotRefOrderLine,
    col,
    _,
    mode?: Imode
  ) => (
    <div
      style={{ display: "flex", flexWrap: "nowrap", justifyContent: "center" }}
    >
      {!row?.underDeliveryTol && !row?.overDeliveryTol && mode?.displayView ? (
        rendrViewContent(false)
      ) : (
        <>
          <MyTypography
            variant="body3"
            align={mode?.displayView ? "center" : "left"}
            style={{ paddingRight: "12px" }}
          >
            {mode?.displayView ||
            (!mode?.displayView &&
              isChangeFlow &&
              mode?.currentAction !== "add" &&
              col?.allowEdit?.has(row?.itemStatusEn))
              ? rendrViewContent(row?.underDeliveryTol)
              : CommonSingleTextFieldUnblock(
                  `lines[${row?.indexNumber}].${"underDeliveryTol"}`,
                  row.underDeliveryTol,
                  handleChange,
                  classes,
                  "Under",
                  disableDeliveryTolerance(),
                  handleKeyDown,
                  `lines[${row?.indexNumber}].underDeliveryTolField`
                )}
          </MyTypography>
          <MyTypography
            variant="body3"
            align={mode?.displayView ? "center" : "left"}
          >
            {mode?.displayView ||
            (!mode?.displayView &&
              isChangeFlow &&
              mode?.currentAction !== "add" &&
              col?.allowEdit?.has(row?.itemStatusEn))
              ? rendrViewContent(row?.overDeliveryTol)
              : CommonSingleTextFieldUnblock(
                  `lines[${row?.indexNumber}].${"overDeliveryTol"}`,
                  row.overDeliveryTol,
                  handleChange,
                  classes,
                  "Over",
                  disableDeliveryTolerance(),
                  handleKeyDown,
                  `lines[${row?.indexNumber}].overDeliveryTolField`
                )}
          </MyTypography>
        </>
      )}
    </div>
  );

  const SaleTextSelection = ({ row, col, title }) => (
    <div
      style={{
        display: "flex",
        flexWrap: "nowrap",
        justifyContent: "space-between"
      }}
    >
      {row?.[col?.id] ? (
        <MyTypography
          variant="body3"
          align="left"
          id={`lines[${row?.indexNumber}].${col.id}`}
          data-testid={`lines[${row?.indexNumber}].${col.id}`}
        >
          {row?.[col?.id]}
        </MyTypography>
      ) : (
        <MyTypography variant="subtitle3" className={classes.fullWidth}>
          -
        </MyTypography>
      )}
      <IconButton
        id={`saleTextSelection-${row?.indexNumber}.${col.id}`}
        data-testid={`saleTextSelection-${row?.indexNumber}.${col.id}`}
        color="primary"
        className={classes.svgIconButton}
        disableRipple
        disableFocusRipple
        disableTouchRipple
        onClick={() =>
          handleOpenSaleTextModal(
            row?.[col?.id],
            title,
            row?.indexNumber,
            col?.id
          )
        }
      >
        <InlineSVG src={pencilIcon} height={18} width={18} />
      </IconButton>
    </div>
  );

  const saleTextFormatter = (title = "Sale Text 1") => (
    row: ChangeNotRefOrderLine,
    col,
    _,
    mode?: Imode
  ) =>
    (mode?.displayView && !row?.isSplit) ||
    (!mode?.displayView &&
      !row?.isSplit &&
      isChangeFlow &&
      mode?.currentAction !== "add" &&
      col?.allowEdit?.has(row?.itemStatusEn)) ? (
      rendrViewContent(row?.[col?.id])
    ) : (
      <SaleTextSelection row={row} col={col} title={title} />
    );

  return {
    quantityFormatter,
    quantityUnitFormatter,
    productionFlagFormatter,
    batchNumberFormatter,
    pricePerUnitFormatter,
    poDetailsFormatter,
    poItemNumberFormatter,
    changeOrderOsFormatter,
    changeOrderOsHeaderFormatter,
    changeOrderItemOsHeaderFormatter,
    requestDateFormatter,
    originalDateFormatter,
    priceDateFormatter,
    unitOfMeasureFormatter,
    unitOfMeasureHeaderFormatter,
    deliveryBlockedFormatter,
    shippingPointFormatter,
    ItemNoFormatter,
    materialDescriptionFormatter,
    textBoxFormatter,
    shipToPartyFormatter,
    lotNoPpFormatter,
    CIPPPFormatter,
    materialCodeFormatter,
    emptyMiddleware,
    CommonDecimalFormatter,
    deliveryToleranceHeaderFormatter,
    deliveryToleranceFormatter,
    usePlantSearchFormatter,
    netPriceFormatter,
    plantApplyAllHeaderFormatter,
    requestDateApplyAllHeaderFormatter,
    priceDateApplyAllHeaderFormatter,
    paymentTermFormatter,
    requiredHeaderFormatter,
    itemStatusFormatter,
    itemCatFormater,
    saleTextFormatter
  };
}

export default useFormatters;
